'use client';
import { useState, useEffect, useCallback } from 'react';
import { FiUsers, FiStar, FiMapPin, FiUser, FiAlertCircle, FiInfo, FiCheckCircle, FiRefreshCw, FiEye, FiZap, FiRepeat } from 'react-icons/fi';
import ContactViewButton from '@/components/ContactViewButton';
import { useContactQuota } from '@/lib/hooks/useContactQuota';
import ViewHistory from '@/components/ViewHistory';
import ChainMatches, { type Chain } from './ChainMatches';

interface QuotaData {
  [featureName: string]: {
    used: number;
    limit: number;
    resetDate: string;
  };
}

interface Match {
  id: number;
  name: string;
  mobile: string;
  gender: string;
  teacher_category: string;
  class: string;
  subject: string;
  current_school_udise: string;
  current_school_name: string;
  school_type: string;
  created_at: string;
  current_district: string;
  current_block: string;
  user_wants_to_go: {
    district: string;
    block: string;
    school: string;
    scope: string;
    preference_order: number;
  };
  match_wants_to_go: {
    district: string;
    block: string;
    school: string;
    scope: string;
    preference_order: number;
  };
  match_score: number;
  unreachable?: boolean;
  match_type?: string;
}

interface SeenContact {
  contact_application_id: number;
  name: string;
  mobile: string;
  gender?: string;
  teacher_category?: string;
  class?: string;
  subject?: string;
  school_type?: string;
  current_school_udise?: string;
  current_school_name?: string;
  created_at?: string;
  district_name?: string;
  block_name?: string;
  latitude?: number | string | null;
  longitude?: number | string | null;
  map_link?: string | null;
  preferences?: { preference_order: number; district: string; block: string; school: string; scope: string }[];
  viewed_at: string;
}

interface Preference {
  preference_order: number;
  district: string;
  block: string;
  school: string;
  scope: string;
}

interface User {
  name: string;
  email: string;
}

type TabKey = 'direct' | 'district' | 'chain' | 'magic' | 'seen';

interface MatchesSectionProps {
  user: User;
  /** Optionally drive the active tab from outside (e.g. the sidebar). */
  tab?: string;
  /** Called when a tab is clicked, so the parent can reflect it in the URL. */
  onTabChange?: (tab: string) => void;
}

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'direct', label: 'Direct', icon: <FiStar size={14} /> },
  { key: 'district', label: 'District', icon: <FiMapPin size={14} /> },
  { key: 'chain', label: '3-Way', icon: <FiRepeat size={14} /> },
  { key: 'magic', label: 'Magic', icon: <FiZap size={14} /> },
  { key: 'seen', label: 'My Seen Contacts', icon: <FiEye size={14} /> },
];

const isTabKey = (t?: string): t is TabKey => TABS.some(x => x.key === t);

export default function MatchesSection({ tab, onTabChange }: MatchesSectionProps) {
  const contactQuota = useContactQuota();
  const [matches, setMatches] = useState<Match[]>([]);
  const [districtMatches, setDistrictMatches] = useState<Match[]>([]);
  const [magicMatches, setMagicMatches] = useState<Match[]>([]);
  const [chainMatches, setChainMatches] = useState<Chain[]>([]);
  const [seenContacts, setSeenContacts] = useState<SeenContact[]>([]);
  const [application, setApplication] = useState<{ preferences?: Preference[] } | null>(null);

  const [loading, setLoading] = useState(true);
  const [districtLoading, setDistrictLoading] = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);
  const [seenLoading, setSeenLoading] = useState(true);
  const [chainLoading, setChainLoading] = useState(false);
  const [districtLoaded, setDistrictLoaded] = useState(false);
  const [magicLoaded, setMagicLoaded] = useState(false);
  const [chainLoaded, setChainLoaded] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [hasApplication, setHasApplication] = useState(false);
  const [pages, setPages] = useState<Record<string, number>>({});
  const [activeTab, setActiveTab] = useState<TabKey>(isTabKey(tab) ? tab : 'direct');

  // The magic link lands on ?section=matches with no tab, so 'direct' is only a
  // provisional default: once the counts arrive, the auto-default effect below
  // moves off an empty Direct tab. True once anything explicit picked a tab —
  // a ?tab= deep link, a sidebar drive, or a click — or once the effect ran.
  const [autoTabDone, setAutoTabDone] = useState(isTabKey(tab));

  // Let the sidebar (via the `tab` prop) drive the active tab.
  // Render-phase adjust-on-change: initial state already derives from `tab`,
  // so this only fires on subsequent `tab` changes (matching the old effect).
  const [prevTab, setPrevTab] = useState(tab);
  if (tab !== prevTab) {
    setPrevTab(tab);
    if (isTabKey(tab)) {
      setActiveTab(tab);
      setAutoTabDone(true);
    }
  }

  // Pending on-demand contact state (deep-linked reveal from pool / magic-match)
  const [pendingOnDemandContact, setPendingOnDemandContact] = useState<{
    id: number;
    name: string;
    mobile: string;
    gender: string;
    teacher_category: string;
    class: string;
    subject: string;
    current_school_udise: string;
    current_school_name: string;
    school_type: string;
    created_at: string;
    current_district: string;
    current_block: string;
  } | null>(null);

  // Quota state
  const [quotaData, setQuotaData] = useState<QuotaData | null>(null);
  const [quotaLoading, setQuotaLoading] = useState(true);
  const [quotaError, setQuotaError] = useState<string | null>(null);
  const [showViewHistory, setShowViewHistory] = useState(false);
  const [viewedContactIds, setViewedContactIds] = useState<Set<number>>(new Set());

  // Fetch quota data
  const fetchQuotaData = async () => {
    try {
      setQuotaLoading(true);
      setQuotaError(null);
      const response = await fetch('/api/user/quota', { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch quota data');
      const result = await response.json();
      if (result.success) {
        setQuotaData(result.data);
      } else {
        throw new Error(result.error || 'Failed to load quota data');
      }
    } catch (err) {
      setQuotaError('Failed to load quota data');
      console.error('Error fetching quota data:', err);
    } finally {
      setQuotaLoading(false);
    }
  };

  // Fetch seen contacts (full list + id set) from view history
  const fetchSeenContacts = async () => {
    try {
      setSeenLoading(true);
      const response = await fetch('/api/user/view-history?type=contacts', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const views: SeenContact[] = (data.data.contactViews || []).map((v: SeenContact & { contact_application_id: string | number }) => ({
            ...v,
            contact_application_id: Number(v.contact_application_id),
          }));
          setSeenContacts(views);
          setViewedContactIds(new Set(views.map(v => v.contact_application_id)));
        }
      }
    } catch (err) {
      console.error('Error fetching seen contacts:', err);
    } finally {
      setSeenLoading(false);
    }
  };

  // Quota utility functions
  const getUsagePercentage = (used: number, limit: number) => Math.min((used / limit) * 100, 100);
  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'text-red-600 bg-red-100';
    if (percentage >= 75) return 'text-yellow-600 bg-yellow-100';
    return 'text-green-600 bg-green-100';
  };
  const getUsageStatus = (percentage: number) => {
    if (percentage >= 90) return { icon: <FiAlertCircle size={16} />, text: 'Critical', color: 'text-red-600' };
    if (percentage >= 75) return { icon: <FiInfo size={16} />, text: 'Warning', color: 'text-yellow-600' };
    return { icon: <FiCheckCircle size={16} />, text: 'Good', color: 'text-green-600' };
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const fetchMatches = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/api/bihar/mutual-transfer/matches', { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch matches');
      const data = await response.json();
      setMatches(data.matches || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch matches');
    } finally {
      setLoading(false);
    }
  };

  const fetchDistrictMatches = async () => {
    try {
      setDistrictLoading(true);
      const response = await fetch('/api/bihar/mutual-transfer/matches?mode=district', { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch district matches');
      const data = await response.json();
      setDistrictMatches(data.matches || []);
    } catch (err) {
      console.error('Failed to fetch district matches:', err);
    } finally {
      setDistrictLoading(false);
      setDistrictLoaded(true);
    }
  };

  const fetchMagicMatches = async () => {
    try {
      setMagicLoading(true);
      const response = await fetch('/api/bihar/magic-match', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ preferences: application?.preferences || [] }),
      });
      if (!response.ok) throw new Error('Failed to fetch magic matches');
      const data = await response.json();
      // Magic tab = only the "adjacent" (nearby-district) tier
      setMagicMatches((data.matches || []).filter((m: Match) => m.match_type === 'adjacent'));
    } catch (err) {
      console.error('Failed to fetch magic matches:', err);
      setMagicMatches([]);
    } finally {
      setMagicLoading(false);
      setMagicLoaded(true);
    }
  };

  const fetchChainMatches = async () => {
    try {
      setChainLoading(true);
      const response = await fetch('/api/bihar/mutual-transfer/chain-matches', { credentials: 'include', cache: 'no-store' });
      if (!response.ok) throw new Error('Failed to fetch chain matches');
      const data = await response.json();
      setChainMatches(data.chains || []);
    } catch (err) {
      console.error('Failed to fetch chain matches:', err);
      setChainMatches([]);
    } finally {
      setChainLoading(false);
      setChainLoaded(true);
    }
  };

  const fetchPendingOnDemandContact = async (applicationId: number) => {
    try {
      const response = await fetch(`/api/bihar/mutual-transfer/applications/${applicationId}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch pending on-demand contact');
      const data = await response.json();
      setPendingOnDemandContact(data.application);
    } catch (err) {
      console.error('Failed to fetch pending on-demand contact:', err);
    }
  };

  // Check if user has a mutual transfer application
  const checkApplication = useCallback(async () => {
    try {
      const response = await fetch('/api/bihar/mutual-transfer', { method: 'GET', credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setApplication(data.application || null);
        setHasApplication(true);
        await fetchMatches();
      } else if (response.status === 404) {
        setHasApplication(false);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error checking application:', error);
      setHasApplication(false);
      setLoading(false);
    }
  }, []);

  // Deep-linked pending on-demand contact from URL (?onDemand=<id>)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const onDemandId = urlParams.get('onDemand');
    if (onDemandId && !isNaN(Number(onDemandId))) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- deep-link load; effect reads DOM and performs async work
      fetchPendingOnDemandContact(Number(onDemandId));
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- initial load; effect performs async work
    checkApplication();
    fetchQuotaData();
    fetchSeenContacts();
  }, [checkApplication]);

  // Every tab carries a count, so every tab's data has to be in hand — a count
  // on Direct but not on 3-Way reads as "3-Way has none", which is a different
  // claim from "not counted yet". So all four load in the background once the
  // application is known, rather than the tab the user happens to open.
  useEffect(() => {
    if (!hasApplication) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- background prefetch; effect performs async work
    if (!districtLoaded && !districtLoading) fetchDistrictMatches();
    if (!magicLoaded && !magicLoading) fetchMagicMatches();
    if (!chainLoaded && !chainLoading) fetchChainMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, hasApplication]);

  // Open the first tab that actually has matches, precedence Direct > District
  // > 3-Way > Magic. A district-only teacher tapping the WhatsApp alert used to
  // land on an empty Direct tab — the exact moment the message promised them a
  // match. Each step waits for its tab's data before ruling it out, so the
  // decision can take a couple of renders; it runs once, and never overrides an
  // explicit choice (autoTabDone above).
  useEffect(() => {
    if (autoTabDone || loading) return;
    const pick = (key: TabKey) => {
      setAutoTabDone(true);
      if (key !== 'direct') {
        setActiveTab(key);
        onTabChange?.(key);
      }
    };
    if (matches.length > 0) return pick('direct');
    if (!districtLoaded) return;
    if (districtMatches.some(dm => !matches.some(m => m.id === dm.id))) return pick('district');
    if (!chainLoaded) return;
    if (chainMatches.length > 0) return pick('chain');
    if (!magicLoaded) return;
    pick(magicMatches.length > 0 ? 'magic' : 'direct');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoTabDone, loading, districtLoaded, chainLoaded, magicLoaded, matches, districtMatches, chainMatches, magicMatches]);

  const maskName = (name: string) => {
    if (!name) return '';
    const parts = name.split(' ');
    if (parts.length === 1) {
      const part = parts[0];
      return part.charAt(0) + '*'.repeat(Math.max(0, part.length - 1));
    }
    const firstPart = parts[0];
    const lastPart = parts[parts.length - 1];
    return firstPart.charAt(0) + '*'.repeat(Math.max(0, firstPart.length - 1)) + ' ' + lastPart.charAt(0) + '*'.repeat(Math.max(0, lastPart.length - 1));
  };

  const isMatchViewed = (matchId: number) => viewedContactIds.has(matchId);

  // District-only matches = present in district mode but NOT in normal (direct) mode
  const districtOnly = districtMatches.filter(dm => !matches.some(m => m.id === dm.id));

  // Magic (adjacent) split into the same two groups the magic-match page uses
  const magicWantTheirs = magicMatches.filter(m => (m.match_score ?? 0) >= 70); // You want their district
  const magicWantYours = magicMatches.filter(m => (m.match_score ?? 0) < 70); // They want your district

  // ---- client-side pagination (100 per page) ----
  const PER_PAGE = 100;
  const getPage = (key: string) => pages[key] || 1;
  const setPage = (key: string, n: number) => setPages(prev => ({ ...prev, [key]: n }));
  const paged = <T,>(arr: T[], key: string): T[] => {
    const totalPages = Math.max(1, Math.ceil(arr.length / PER_PAGE));
    const page = Math.min(getPage(key), totalPages);
    return arr.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  };
  const renderPager = (key: string, total: number) => {
    const totalPages = Math.ceil(total / PER_PAGE);
    if (totalPages <= 1) return null;
    const page = Math.min(getPage(key), totalPages);
    return (
      <div className="flex items-center justify-center gap-3 pt-2">
        <button
          disabled={page <= 1}
          onClick={() => setPage(key, page - 1)}
          className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          ← Prev
        </button>
        <span className="text-xs text-slate-600">
          Page <span className="font-semibold">{page}</span> / {totalPages} · {total} total
        </span>
        <button
          disabled={page >= totalPages}
          onClick={() => setPage(key, page + 1)}
          className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Next →
        </button>
      </div>
    );
  };

  const handleRefresh = async () => {
    if (hasApplication) {
      await fetchMatches();
      if (districtLoaded) await fetchDistrictMatches();
      if (magicLoaded) await fetchMagicMatches();
      if (chainLoaded) await fetchChainMatches();
    }
    await fetchQuotaData();
    await fetchSeenContacts();
  };

  const handleChainRevealed = () => {
    fetchQuotaData();
    fetchSeenContacts();
    fetchChainMatches();
  };

  const handleContactViewed = () => {
    fetchQuotaData();
    fetchSeenContacts();
  };

  const handlePendingContactViewed = () => {
    if (pendingOnDemandContact) {
      setPendingOnDemandContact(null);
      const url = new URL(window.location.href);
      url.searchParams.delete('onDemand');
      window.history.replaceState({}, '', url.toString());
    }
    fetchQuotaData();
    fetchSeenContacts();
  };

  // ---- reusable match card (direct / district / magic) ----
  const renderMatchCard = (match: Match, kind: 'perfect' | 'district' | 'adjacent') => {
    const badge =
      kind === 'district'
        ? { cls: 'bg-purple-100 text-purple-700', icon: <FiMapPin size={10} />, label: 'District Match', border: 'border-purple-200 bg-purple-50/30', score: 'text-purple-600', denom: '/15' }
        : kind === 'adjacent'
        ? { cls: 'bg-amber-100 text-amber-700', icon: <FiZap size={10} />, label: 'Magic Match', border: 'border-amber-200 bg-amber-50/30', score: 'text-amber-600', denom: '/100' }
        : { cls: 'bg-green-100 text-green-700', icon: <FiStar size={10} />, label: 'Perfect Match', border: 'border-green-200 bg-green-50/30', score: 'text-blue-600', denom: '/30' };

    return (
      <div key={match.id} className={`bg-white border rounded-lg p-4 shadow-sm ${badge.border}`}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center text-slate-600">
              <FiUser size={16} />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 text-sm">
                {maskName(match.name)}
                <span className={`ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded-full ${badge.cls}`}>
                  {badge.icon}
                  {badge.label}
                </span>
              </h3>
              <p className="text-xs text-slate-500">
                {match.teacher_category} • {match.class} • {match.subject}
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500">Match Score</div>
            <div className={`text-base font-semibold ${badge.score}`}>
              {match.match_score}
              <span className="text-xs text-slate-500 ml-1">{badge.denom}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <h4 className="font-medium text-slate-900 mb-1 text-sm">Current Location</h4>
            <p className="text-xs text-slate-600">{match.current_school_name}</p>
            <p className="text-xs text-slate-500">{match.current_district}, {match.current_block}</p>
          </div>
          <div>
            <h4 className="font-medium text-slate-900 mb-1 text-sm">Wants to Transfer to</h4>
            <p className="text-xs text-slate-600">
              {match.match_wants_to_go?.scope === 'school' ? match.match_wants_to_go.school :
               match.match_wants_to_go?.scope === 'block' ? `${match.match_wants_to_go.block} Block` :
               `${match.match_wants_to_go?.district} District`}
            </p>
            {match.match_wants_to_go?.preference_order != null && (
              <p className="text-xs text-slate-500">Preference #{match.match_wants_to_go.preference_order}</p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 pt-3 border-t border-slate-200">
          {/* min-w-0 lets the meta line shrink first, so the button never gets squeezed into wrapping */}
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500">
            <span className="whitespace-nowrap">Applied: {formatDate(match.created_at)}</span>
            <span aria-hidden="true">•</span>
            <span>{match.gender}</span>
            {/* Sits in the existing meta line so the card height doesn't change. */}
            {match.unreachable && (
              <span className="whitespace-nowrap font-medium text-amber-700">
                • Unreachable
              </span>
            )}
          </div>
          <ContactViewButton
            contactApplicationId={match.id}
            onContactViewed={handleContactViewed}
            className="shrink-0 px-2.5 py-1.5 text-xs"
            alreadyViewed={isMatchViewed(match.id)}
            unreachable={match.unreachable}
          />
        </div>
      </div>
    );
  };

  const loadingCard = (text = 'Loading matches...') => (
    <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm text-center">
      <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
      <p className="text-slate-600 text-sm">{text}</p>
    </div>
  );

  const emptyCard = (title: string, subtitle: string, withShare = false) => (
    <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm text-center">
      <div className="w-14 h-14 bg-gradient-to-r from-blue-100 to-cyan-100 rounded-full flex items-center justify-center mx-auto mb-3">
        <FiUsers className="w-7 h-7 text-blue-600" />
      </div>
      <h3 className="text-base font-semibold text-slate-900 mb-1">{title}</h3>
      <p className="text-slate-600 text-sm mb-3">{subtitle}</p>
      {withShare && (
        <button
          onClick={async () => {
            try {
              const response = await fetch('/api/bihar/mutual-transfer/share', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
              });
              const result = await response.json();
              if (response.ok) {
                window.open(`https://wa.me/?text=${encodeURIComponent(result.whatsappMessage)}`, '_blank');
              } else {
                alert('Failed to generate share link. Please try again.');
              }
            } catch {
              alert('Failed to share profile. Please try again.');
            }
          }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.786"/>
          </svg>
          Share Profile on WhatsApp
        </button>
      )}
    </div>
  );

  if (!hasApplication && !loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900">My Matches</h1>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-8 shadow-sm text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiUsers className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Application Found</h3>
          <p className="text-slate-600 mb-4">You need to submit a mutual transfer application to see your matches.</p>
          <a href="/bihar/mutual-transfer" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            Submit Application
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Quota Section */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-semibold text-slate-900">Usage Quota</h2>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setShowViewHistory(!showViewHistory)}
              className="flex items-center gap-1 rounded-lg bg-blue-100 px-2 py-1 text-xs text-blue-700 transition-colors hover:bg-blue-200"
            >
              <FiEye size={12} />
              View History
            </button>
            <button
              onClick={handleRefresh}
              disabled={loading || quotaLoading}
              className="flex items-center gap-1 rounded-lg bg-slate-200 px-2 py-1 text-xs text-slate-700 transition-colors hover:bg-slate-300 disabled:opacity-50"
            >
              <FiRefreshCw className={`h-3 w-3 ${(loading || quotaLoading) ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {quotaLoading ? (
          <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm text-center">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-slate-600 text-sm">Loading quota data...</p>
          </div>
        ) : quotaError ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600 text-sm">{quotaError}</p>
          </div>
        ) : quotaData && Object.keys(quotaData).length > 0 ? (
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {Object.entries(quotaData).map(([featureName, quota]) => {
              const getFeatureIcon = (name: string) => {
                switch (name) {
                  case 'contact_numbers': return <FiUsers className="w-4 h-4 text-blue-600" />;
                  case 'maps': return <FiMapPin className="w-4 h-4 text-green-600" />;
                  default: return <FiEye className="w-4 h-4 text-purple-600" />;
                }
              };
              const getFeatureColor = (name: string) => {
                switch (name) {
                  case 'contact_numbers': return 'bg-blue-100';
                  case 'maps': return 'bg-green-100';
                  default: return 'bg-purple-100';
                }
              };
              const getFeatureDisplayName = (name: string) => {
                switch (name) {
                  case 'contact_numbers': return 'Contacts';
                  case 'maps': return 'Map Page Access';
                  default: return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                }
              };
              const percentage = getUsagePercentage(quota.used, quota.limit);
              const status = getUsageStatus(percentage);
              return (
                <div key={featureName} className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
                  <div className="flex items-center gap-2">
                    <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${getFeatureColor(featureName)}`}>
                      {getFeatureIcon(featureName)}
                    </span>
                    <h3 className="truncate text-sm font-semibold text-slate-900">{getFeatureDisplayName(featureName)}</h3>
                    <span className="ml-auto text-sm font-semibold tabular-nums text-slate-900">
                      {quota.used} / {quota.limit}
                    </span>
                    <span className={`flex-shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${getUsageColor(percentage)}`}>
                      {status.text}
                    </span>
                  </div>
                  <div className="mt-2 h-1 w-full rounded-full bg-slate-200">
                    <div
                      className={`h-1 rounded-full transition-all duration-300 ${
                        percentage >= 90 ? 'bg-red-500' : percentage >= 75 ? 'bg-yellow-500' : featureName === 'contact_numbers' ? 'bg-blue-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
            <p className="text-[11px] text-slate-400 md:col-span-2">Unlimited validity — quota never expires</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm text-center">
            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <FiEye className="w-6 h-6 text-slate-400" />
            </div>
            <h3 className="text-base font-semibold text-slate-900 mb-2">No Active Subscription</h3>
            <p className="text-slate-600 text-sm mb-4">You need to purchase a plan to access quota features.</p>
            <a href="/pricing" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              View Plans
            </a>
          </div>
        )}
      </div>

      {/* View History Section */}
      {showViewHistory && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">View History</h2>
          <ViewHistory />
        </div>
      )}

      {/* Pending On-Demand Contact (deep-linked reveal) */}
      {pendingOnDemandContact && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-base font-semibold text-blue-900">Pending Contact — ready to view</h2>
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">Ready</span>
          </div>
          <div className="bg-white border border-blue-200 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <h3 className="font-semibold text-slate-900 text-sm">{pendingOnDemandContact.name}</h3>
              <p className="text-xs text-slate-500">
                {pendingOnDemandContact.teacher_category} • {pendingOnDemandContact.class} • {pendingOnDemandContact.subject} • {pendingOnDemandContact.current_district}, {pendingOnDemandContact.current_block}
              </p>
            </div>
            <ContactViewButton
              contactApplicationId={pendingOnDemandContact.id}
              onContactViewed={handlePendingContactViewed}
              className="px-3 py-1.5 text-xs shrink-0"
            />
          </div>
        </div>
      )}

      {/* Matches with tabs */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <h1 className="text-xl font-bold text-slate-900">My Matches</h1>
          {/* Contacts left is one fact about the user — stated once, not on every card */}
          {contactQuota && contactQuota.limit > 0 && (
            <span
              className={`text-xs font-medium tabular-nums ${
                contactQuota.remaining === 0 ? 'text-amber-700' : 'text-slate-500'
              }`}
            >
              {contactQuota.remaining === 0
                ? 'No contacts left'
                : `${contactQuota.remaining} of ${contactQuota.limit} contacts left`}
            </span>
          )}
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 border-b border-slate-200 overflow-x-auto -mb-px">
          {TABS.map(tab => {
            const active = activeTab === tab.key;
            const count =
              tab.key === 'direct' ? matches.length :
              tab.key === 'district' ? (districtLoaded ? districtOnly.length : null) :
              tab.key === 'magic' ? (magicLoaded ? magicMatches.length : null) :
              tab.key === 'chain' ? (chainLoaded ? chainMatches.length : null) :
              tab.key === 'seen' ? seenContacts.length : null;
            return (
              <button
                key={tab.key}
                onClick={() => { setAutoTabDone(true); setActiveTab(tab.key); onTabChange?.(tab.key); }}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  active ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
                }`}
              >
                {tab.icon}
                {tab.label}
                {/* Once a tab's data is in, its count is shown even when it is
                    zero — a missing badge next to a "2" reads as "none here",
                    which is a claim we cannot make until the fetch lands. */}
                {count != null && (
                  <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                    count === 0 ? 'bg-slate-100 text-slate-400' :
                    active ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Direct */}
        {activeTab === 'direct' && (
          loading ? loadingCard() :
          error ? <div className="bg-red-50 border border-red-200 rounded-lg p-4"><p className="text-red-600">{error}</p></div> :
          matches.length === 0 ? emptyCard('No direct matches yet', 'No exact reciprocal swaps found yet. Share your profile to find matches faster!', true) :
          <div className="space-y-3">
            {paged(matches, 'direct').map(m => renderMatchCard(m, 'perfect'))}
            {renderPager('direct', matches.length)}
          </div>
        )}

        {/* District */}
        {activeTab === 'district' && (
          districtLoading ? loadingCard() :
          districtOnly.length === 0 ? emptyCard('No district matches', 'No same-district (block/school-relaxed) matches found yet.') :
          <div className="space-y-3">
            {paged(districtOnly, 'district').map(m => renderMatchCard(m, 'district'))}
            {renderPager('district', districtOnly.length)}
          </div>
        )}

        {/* 3-Way */}
        {activeTab === 'chain' && (
          <ChainMatches
            showcase
            autoLoad
            chains={chainMatches}
            loadingProp={chainLoading && !chainLoaded}
            onRevealed={handleChainRevealed}
          />
        )}

        {/* Magic */}
        {activeTab === 'magic' && (
          magicLoading ? loadingCard('Finding magic matches...') :
          magicMatches.length === 0 ? emptyCard('No magic matches', 'No nearby / adjacent-district swaps found yet.') :
          <div className="space-y-5">
            {magicWantTheirs.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                  <FiZap size={14} /> You Want Their District
                  <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-bold">{magicWantTheirs.length}</span>
                </h3>
                {paged(magicWantTheirs, 'magic-theirs').map(m => renderMatchCard(m, 'adjacent'))}
                {renderPager('magic-theirs', magicWantTheirs.length)}
              </div>
            )}
            {magicWantYours.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                  <FiMapPin size={14} /> They Want Your District
                  <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-bold">{magicWantYours.length}</span>
                </h3>
                {paged(magicWantYours, 'magic-yours').map(m => renderMatchCard(m, 'adjacent'))}
                {renderPager('magic-yours', magicWantYours.length)}
              </div>
            )}
          </div>
        )}

        {/* My Seen Contacts */}
        {activeTab === 'seen' && (
          seenLoading ? loadingCard('Loading your seen contacts...') :
          seenContacts.length === 0 ? emptyCard('No seen contacts yet', 'Contacts you unlock will appear here with their phone numbers.') :
          <div className="space-y-3">
            {paged(seenContacts, 'seen').map(c => {
              // Open our own school-maps viewer (searches by UDISE), not Google Maps
              const mapUrl = c.current_school_udise ? `/bihar/school-maps?search=${encodeURIComponent(c.current_school_udise)}` : null;
              const prefText = (p: { district: string; block: string; school: string; scope: string }) =>
                p.scope === 'district' ? `${p.district} (Any block/school)` :
                p.scope === 'block' ? `${p.district} → ${p.block} (Any school)` :
                `${p.district} → ${p.block} → ${p.school}`;
              // Small OpenStreetMap preview (overall zoom, not fully zoomed in); click opens our mapper
              const lat = Number(c.latitude), lon = Number(c.longitude);
              const hasMap = c.latitude != null && c.longitude != null && !Number.isNaN(lat) && !Number.isNaN(lon);
              const d = 0.02;
              const osm = hasMap
                ? `https://www.openstreetmap.org/export/embed.html?bbox=${lon - d}%2C${lat - d}%2C${lon + d}%2C${lat + d}&layer=mapnik&marker=${lat}%2C${lon}`
                : null;
              return (
                <div key={`${c.contact_application_id}-${c.viewed_at}`} className="bg-white border border-emerald-200 rounded-lg p-4 shadow-sm">
                  <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 min-w-0">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600">
                        <FiUser size={16} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 text-sm">
                          {c.name}
                          <span className="ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                            <FiCheckCircle size={10} /> Seen
                          </span>
                        </h3>
                        <p className="text-xs text-slate-500">
                          {[c.teacher_category, c.class, c.subject, c.school_type].filter(Boolean).join(' • ')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">Contact</div>
                      <a href={`tel:${c.mobile}`} className="text-sm font-bold text-emerald-700">{c.mobile}</a>
                    </div>
                  </div>

                  {/* Current location + where they want to go */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <h4 className="font-medium text-slate-900 mb-1 text-sm">Current Location</h4>
                      <p className="text-xs text-slate-600">{c.current_school_name || '—'}</p>
                      <p className="text-xs text-slate-500">
                        {[c.district_name, c.block_name].filter(Boolean).join(', ') || '—'}
                        {c.current_school_udise ? ` • UDISE ${c.current_school_udise}` : ''}
                      </p>
                      {!osm && mapUrl && (
                        <a href={mapUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-1 text-xs text-blue-600 hover:underline">
                          <FiMapPin size={12} /> View on Map
                        </a>
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900 mb-1 text-sm">Wants to Transfer to</h4>
                      {c.preferences && c.preferences.length > 0 ? (
                        <ol className="space-y-0.5">
                          {c.preferences.map(p => (
                            <li key={p.preference_order} className="text-xs text-slate-600">
                              <span className="text-slate-400">#{p.preference_order}</span> {prefText(p)}
                            </li>
                          ))}
                        </ol>
                      ) : (
                        <p className="text-xs text-slate-500">Not available</p>
                      )}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-2 border-t border-slate-200 text-xs text-slate-500">
                    <div className="flex items-center gap-3">
                      {c.created_at && <span>Applied: {formatDate(c.created_at)}</span>}
                      {c.gender && <><span>•</span><span>{c.gender}</span></>}
                    </div>
                    <span>Seen: {formatDate(c.viewed_at)}</span>
                  </div>
                  </div>{/* /flex-1 */}

                  {osm && (
                    <a
                      href={mapUrl || osm}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open in map"
                      className="relative block sm:w-56 shrink-0 h-32 sm:h-auto sm:self-stretch min-h-[8rem] rounded-lg overflow-hidden border border-slate-200 group"
                    >
                      <iframe
                        src={osm}
                        title={`Map of ${c.current_school_name || 'school'}`}
                        className="w-full h-full pointer-events-none"
                        loading="lazy"
                      />
                      <span className="absolute inset-0 flex items-end justify-center pb-1.5 bg-black/0 group-hover:bg-black/10 transition-colors">
                        <span className="inline-flex items-center gap-1 text-[10px] bg-white/90 text-blue-700 font-medium px-2 py-0.5 rounded-full shadow">
                          <FiMapPin size={10} /> Open map
                        </span>
                      </span>
                    </a>
                  )}
                  </div>{/* /flex row */}
                </div>
              );
            })}
            {renderPager('seen', seenContacts.length)}
          </div>
        )}
      </div>
    </div>
  );
}
